import { readBrowserSafeEnv } from "@/lib/browser-safe-env";

export const MEDIA_UPLOAD_SCOPE_DOMAINS = [
  "profiles",
  "hiives",
  "events",
  "venues",
  "posts",
  "reviews",
  "cards",
  "offers",
  "chats",
  "stories",
  "instants",
] as const;

export type MediaUploadScopeDomain =
  (typeof MEDIA_UPLOAD_SCOPE_DOMAINS)[number];

const DISALLOWED_PUBLIC_BASE_HOST_SUFFIX = ".r2.cloudflarestorage.com";

const VIDEO_EXTENSIONS = new Set([
  "mp4",
  "webm",
  "mov",
  "3gp",
  "3gpp",
  "m4v",
  "m3u8",
]);

const sanitizeScopeSegment = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 120) || "unknown";

const inferExtension = (fileName: string, mimeType: string): string => {
  const normalized = fileName.toLowerCase();
  const fileMatch = normalized.match(/\.([a-z0-9]+)$/);
  if (fileMatch?.[1]) {
    return fileMatch[1];
  }

  if (mimeType.includes("avif")) return "avif";
  if (mimeType.includes("webp")) return "webp";
  if (mimeType.includes("png")) return "png";
  if (mimeType.includes("gif")) return "gif";
  if (mimeType.includes("webm")) return "webm";
  if (mimeType.includes("quicktime")) return "mov";
  if (mimeType.includes("mp4")) return "mp4";
  if (mimeType.includes("mpeg")) return "mp3";
  if (mimeType.includes("mp3")) return "mp3";
  if (mimeType.includes("wav")) return "wav";
  if (mimeType.includes("aac")) return "aac";
  if (mimeType.includes("ogg")) return "ogg";
  if (mimeType.includes("m4a")) return "m4a";
  if (mimeType.includes("jpeg") || mimeType.includes("jpg")) return "jpg";

  return "bin";
};

export const buildMediaKey = (input: {
  domain: MediaUploadScopeDomain;
  entityRef: string;
  slot: string;
  assetId: string;
  fileName: string;
  mimeType: string;
}): string => {
  const domain = sanitizeScopeSegment(input.domain);
  const entityRef = sanitizeScopeSegment(input.entityRef);
  const slot = sanitizeScopeSegment(input.slot);
  const extension = inferExtension(input.fileName, input.mimeType);
  return `${domain}/${entityRef}/${slot}/${input.assetId}.${extension}`;
};

export const normalizePublicMediaBaseUrl = (rawBaseUrl: string): string => {
  let parsed: URL;
  try {
    parsed = new URL(rawBaseUrl);
  } catch {
    throw new Error(
      "R2 public media base URL must be a valid URL (recommended: custom CDN domain or *.r2.dev)",
    );
  }

  if (parsed.protocol !== "https:" && parsed.protocol !== "http:") {
    throw new Error("R2 public media base URL must use http:// or https://");
  }

  if (
    parsed.hostname.toLowerCase().endsWith(DISALLOWED_PUBLIC_BASE_HOST_SUFFIX)
  ) {
    throw new Error(
      "R2 public media base URL cannot use *.r2.cloudflarestorage.com. Use a custom CDN domain or *.r2.dev instead.",
    );
  }

  const normalizedPath =
    parsed.pathname && parsed.pathname !== "/"
      ? parsed.pathname.replace(/\/+$/, "")
      : "";

  return `${parsed.origin}${normalizedPath}`;
};

const resolveConfiguredPublicMediaBaseUrl = (): string => {
  const configured = readBrowserSafeEnv("NEXT_PUBLIC_R2_PUBLIC_BASE_URL");

  if (!configured) {
    throw new Error(
      "R2 public media base URL is not configured. Set NEXT_PUBLIC_R2_PUBLIC_BASE_URL.",
    );
  }

  return normalizePublicMediaBaseUrl(configured);
};

export const mediaUrlFromKey = (key: string, baseUrl?: string): string => {
  const normalizedKey = key.replace(/^\/+/, "");
  const resolvedBase = baseUrl
    ? normalizePublicMediaBaseUrl(baseUrl)
    : resolveConfiguredPublicMediaBaseUrl();
  return `${resolvedBase}/${normalizedKey}`;
};

export const mediaPosterUrlFromKey = (
  posterKey: string,
  baseUrl?: string,
): string => mediaUrlFromKey(posterKey, baseUrl);

export const keyFromMediaUrl = (url: string): string | undefined => {
  try {
    const parsed = new URL(url);
    const key = decodeURIComponent(parsed.pathname.replace(/^\/+/, ""));
    return key.length > 0 ? key : undefined;
  } catch {
    return undefined;
  }
};

export const extensionFromKey = (key: string): string | undefined => {
  const last = key.split("/").pop();
  if (!last) {
    return undefined;
  }

  const index = last.lastIndexOf(".");
  if (index < 0 || index === last.length - 1) {
    return undefined;
  }

  return last.slice(index + 1).toLowerCase();
};

export const mediaIdFromKey = (key: string): string => {
  const last = key.split("/").pop() ?? key;
  const extension = extensionFromKey(last);
  if (!extension) {
    return last;
  }

  return last.slice(0, -(extension.length + 1));
};

export const isVideoKey = (key: string): boolean => {
  const extension = extensionFromKey(key);
  if (!extension) {
    return false;
  }

  return VIDEO_EXTENSIONS.has(extension);
};
